import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { useUpdatePatientMutation, useGetPatientQuery } from "store";
import {
    Box,
    Spinner,
    Center,
    SimpleGrid,
    Card,
    CardBody,
    CardHeader,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    Select,
    Checkbox,
    Textarea,
    Button,
    Heading,
    Text,
} from "@chakra-ui/react";

const UpdatePatient = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: patient, error, isFetching } = useGetPatientQuery(id);

    const parseHeight = (heightValue) => {
        if (!heightValue) return { feet: 0, inches: 0 };
        const isFeetDecimal = heightValue % 1 !== 0 && heightValue < 20; // Heuristic for feet
        if (isFeetDecimal) {
            const feet = Math.floor(heightValue);
            const inches = Math.round((heightValue - feet) * 12);
            return { feet, inches };
        }
        const feet = Math.floor(heightValue / 12);
        const inches = Math.round(heightValue % 12);
        return { feet, inches };
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
    } = useForm({
        defaultValues: {
            ...patient,
            heightFeet: parseHeight(patient?.height).feet,
            heightInches: parseHeight(patient?.height).inches,
        },
    });

    const [
        updatePatient,
        { isLoading, isError, isSuccess },
    ] = useUpdatePatientMutation();

    const [weight, setWeight] = useState(patient?.weight || 0);
    const feet = parseFloat(watch("heightFeet")) || 0;
    const inches = parseFloat(watch("heightInches")) || 0;
    const totalInches = feet * 12 + inches;
    const [bmi, setBmi] = useState(0);
    const [dob, setDob] = useState(patient?.date_of_birth || "");
    const [age, setAge] = useState("");

    useEffect(() => {
        if (patient) {
            const { feet, inches } = parseHeight(patient.height);
            reset({
                ...patient,
                heightFeet: feet,
                heightInches: inches,
            });
        }
    }, [patient, reset]);

    const onSubmit = async (data) => {
        const feet = Number(data.heightFeet) || 0;
        const inches = Number(data.heightInches) || 0;
        const totalInches = feet * 12 + inches;

        const payload = {
            ...data,
            height: totalInches,
        };
        delete payload.heightFeet;
        delete payload.heightInches;

        try {
            await updatePatient({ ...payload, id });
            navigate(`/patients/${id}/details`);
        } catch (error) {
            console.error("Failed to update patient:", error);
        }
    };

    if (isFetching) {
        return (
            <Center>
                <Spinner size="xl" />
            </Center>
        );
    }

    if (error) {
        return <p>Error loading patient data</p>;
    }

    return (
        <Box mt={20} p={4}>
            <Card
                boxShadow="md"
                borderRadius="2xl"
                overflow="hidden"
                p={4}
                border="1px solid"
                borderColor="gray.300"
            >
                <CardHeader borderBottom="1px solid" borderColor="blue.200">
                    <Box textAlign="center" mb={4}>
                        <Text fontSize="2xl" fontWeight="bold">
                            Update Patient Form
                        </Text>
                    </Box>
                </CardHeader>

                <CardBody>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {/* Form fields with register and error handling */}
                        {/* General Information */}
                        <Heading as="h5" size="lg" mb={3}>
                            General Information
                        </Heading>

                        {/* Row 1 */}
                        <SimpleGrid
                            columns={{ base: 1, md: 3 }}
                            spacing={4}
                            mb={6}
                        >
                            {/* Surname */}
                            <Box>
                                <FormControl isInvalid={errors.surname}>
                                    <FormLabel>Surname</FormLabel>
                                    <Input
                                        type="text"
                                        {...register("surname", {
                                            required: true,
                                        })}
                                    />
                                    {errors.surname && (
                                        <FormErrorMessage>
                                            Surname is required
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            </Box>

                            {/* Name */}
                            <Box>
                                <FormControl isInvalid={errors.name}>
                                    <FormLabel>Name</FormLabel>
                                    <Input
                                        type="text"
                                        {...register("name", {
                                            required: true,
                                        })}
                                    />
                                    {errors.name && (
                                        <FormErrorMessage>
                                            Name is required
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            </Box>

                            {/* Address */}
                            <Box>
                                <FormControl>
                                    <FormLabel>Address</FormLabel>
                                    <Input
                                        type="text"
                                        {...register("address")}
                                    />
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        {/* Row 2 */}
                        <SimpleGrid
                            columns={{ base: 1, md: 3 }}
                            spacing={4}
                            mb={6}
                        >
                            {/* Sex */}
                            <Box>
                                <FormControl>
                                    <FormLabel>Sex</FormLabel>
                                    <Select {...register("sex")}>
                                        <option value="M">Male</option>
                                        <option value="F">Female</option>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Nationality */}
                            <Box>
                                <FormControl>
                                    <FormLabel>Nationality</FormLabel>
                                    <Input
                                        type="text"
                                        {...register("nationality")}
                                    />
                                </FormControl>
                            </Box>

                            {/* Telephone Number */}
                            <Box>
                                <FormControl
                                    isInvalid={errors.telephone_number}
                                >
                                    <FormLabel>Telephone Number</FormLabel>
                                    <Input
                                        type="tel"
                                        {...register("telephone_number", {
                                            valueAsNumber: true,
                                        })}
                                    />
                                    {errors.telephone_number && (
                                        <FormErrorMessage>
                                            Telephone number is required
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        {/* Row 3 */}
                        <SimpleGrid
                            columns={{ base: 1, md: 3 }}
                            spacing={4}
                            mb={6}
                        >
                            {/* D.O.B */}
                            <Box>
                                <FormControl isInvalid={errors.date_of_birth}>
                                    <FormLabel>Date of Birth</FormLabel>
                                    <Input
                                        type="date"
                                        {...register("date_of_birth", {
                                            required: true,
                                        })}
                                        onChange={(e) => setDob(e.target.value)}
                                    />
                                    {errors.date_of_birth && (
                                        <FormErrorMessage>
                                            Date of Birth is required
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            </Box>

                            {/* Age */}
                            <Box>
                                <FormControl>
                                    <FormLabel>Age</FormLabel>
                                    <Input type="text" value={age} isDisabled />
                                </FormControl>
                            </Box>

                            {/* Marital Status */}
                            <Box>
                                <FormControl>
                                    <FormLabel>Marital Status</FormLabel>
                                    <Select {...register("marital_status")}>
                                        <option value="S">Single</option>
                                        <option value="M">Married</option>
                                        <option value="W">Widowed</option>
                                        <option value="D">Divorced</option>
                                    </Select>
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        {/* Row 4 */}
                        <SimpleGrid
                            columns={{ base: 1, md: 3 }}
                            spacing={4}
                            mb={6}
                        >
                            {/* Weight */}
                            <Box>
                                <FormControl isInvalid={errors.weight}>
                                    <FormLabel>Weight (lbs)</FormLabel>
                                    <Input
                                        type="number"
                                        step="any"
                                        {...register("weight", {
                                            required: true,
                                        })}
                                        onChange={(e) =>
                                            setWeight(
                                                parseFloat(e.target.value)
                                            )
                                        }
                                    />
                                    {errors.weight && (
                                        <FormErrorMessage>
                                            Weight is required
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            </Box>

                            {/* Height */}
                            <Box>
                                <FormControl isInvalid={errors.heightFeet}>
                                    <FormLabel>Height (ft)</FormLabel>
                                    <Input
                                        type="number"
                                        step="1"
                                        min={0}
                                        {...register("heightFeet", {
                                            required: "Feet is required",
                                            valueAsNumber: true,
                                        })}
                                    />
                                    <FormErrorMessage>
                                        {errors.heightFeet && errors.heightFeet.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </Box>

                            {/* Height — Inches */}
                            <Box>
                                <FormControl isInvalid={errors.heightInches}>
                                    <FormLabel>Height (in)</FormLabel>
                                    <Input
                                        type="number"
                                        step="1"
                                        min={0}
                                        max={11}
                                        {...register("heightInches", {
                                            required: "Inches is required",
                                            valueAsNumber: true,
                                        })}
                                    />
                                    <FormErrorMessage>
                                        {errors.heightInches && errors.heightInches.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </Box>

                            {/* BMI */}
                            <Box>
                                <FormControl>
                                    <FormLabel>BMI</FormLabel>
                                    <Input type="text" value={bmi} isDisabled />
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        {/* Row 5 */}
                        <SimpleGrid
                            columns={{ base: 1, md: 3 }}
                            spacing={4}
                            mb={6}
                        >
                            {/* N.O.K */}
                            <Box>
                                <FormControl>
                                    <FormLabel>Next of Kin</FormLabel>
                                    <Input
                                        type="text"
                                        {...register("next_of_kin")}
                                    />
                                </FormControl>
                            </Box>

                            {/* Relation N.O.K */}
                            <Box>
                                <FormControl>
                                    <FormLabel>
                                        Relation to Next of Kin
                                    </FormLabel>
                                    <Input
                                        type="text"
                                        {...register("relation_nok")}
                                    />
                                </FormControl>
                            </Box>

                            {/* # of N.O.K */}
                            <Box>
                                <FormControl>
                                    <FormLabel>
                                        Telephone Number (Next of Kin)
                                    </FormLabel>
                                    <Input
                                        type="tel"
                                        {...register("telephone_nok", {
                                            valueAsNumber: true,
                                        })}
                                    />
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        {/* Row 6 */}
                        <SimpleGrid
                            columns={{ base: 1, md: 3 }}
                            spacing={4}
                            mb={6}
                        >
                            {/* Occupation */}
                            <Box>
                                <FormControl>
                                    <FormLabel>Occupation</FormLabel>
                                    <Input
                                        type="text"
                                        {...register("occupation")}
                                    />
                                </FormControl>
                            </Box>

                            {/* Is New */}
                            <Box>
                                <FormControl>
                                    <FormLabel>New Patient</FormLabel>
                                    <Checkbox
                                        {...register("is_new")}
                                        label="Yes"
                                    />
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        {/* Row 7 */}
                        <SimpleGrid
                            columns={{ base: 1, md: 1 }}
                            spacing={4}
                            mb={6}
                        >
                            <Box>
                                <FormControl>
                                    <FormLabel>Complaint</FormLabel>
                                    <Textarea
                                        as="textarea"
                                        rows={4}
                                        {...register("complaint")}
                                    />
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        <hr className="mb-2" />

                        {/* Medical History */}
                        <Heading as="h5" size="lg" my={3}>
                            Past Medical History
                        </Heading>

                        {/* Row 8 */}
                        <SimpleGrid
                            columns={{ base: 1, md: 3 }}
                            spacing={4}
                            mb={6}
                        >
                            {/* Group 1 */}
                            <div className="col-4">
                                {/* Diabetes */}
                                <FormControl className="mb-3">
                                    <FormLabel>Diabetes</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("diabetes")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Heart Mumur */}
                                <FormControl className="mb-3">
                                    <FormLabel>Heart Murmur</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("heart_murmur")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Deep Vein Thrombosis */}
                                <FormControl className="mb-3">
                                    <FormLabel>Deep Vein Thrombosis</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("deep_vein_thrombosis")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* HBP */}
                                <FormControl className="mb-3">
                                    <FormLabel>High Blood Pressure</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("high_blood_pressure")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Pneumonia */}
                                <FormControl className="mb-3">
                                    <FormLabel>Pneumonia</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("pneumonia")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Prostate */}
                                <FormControl className="mb-3">
                                    <FormLabel>Prostate</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("prostate_problems")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* High Cholesterol */}
                                <FormControl className="mb-3">
                                    <FormLabel>High Cholesterol</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("high_cholesterol")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Pulmonary Embolism */}
                                <FormControl className="mb-3">
                                    <FormLabel>Pulmonary Embolism</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("pulmonary_embolism")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Anemia */}
                                <FormControl className="mb-3">
                                    <FormLabel>Anemia</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("anemia")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Hypothyroidism */}
                                <FormControl className="mb-3">
                                    <FormLabel>Hypothyroidism</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("hypothyroidism")}
                                        label="Yes"
                                    />
                                </FormControl>
                            </div>

                            {/* Group 2 */}
                            <div className="col-4">
                                {/* Asthma */}
                                <FormControl className="mb-3">
                                    <FormLabel>Asthma</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("asthma")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Juandice */}
                                <FormControl className="mb-3">
                                    <FormLabel>Juandice</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("jaundice")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Hyperthyroidism */}
                                <FormControl className="mb-3">
                                    <FormLabel>Hyperthyroidism</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("hyperthyroidism")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Emphysema Bronchitis */}
                                <FormControl className="mb-3">
                                    <FormLabel>Emphysema Bronchitis</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("emphysema_bronchitis")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Hepatitis */}
                                <FormControl className="mb-3">
                                    <FormLabel>Hepatitis</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("hepatitis")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Cancer */}
                                <FormControl className="mb-3">
                                    <FormLabel>
                                        Cancer Type (if applicable)
                                    </FormLabel>
                                    <FormControl
                                        type="text"
                                        {...register("cancer_type")}
                                    />
                                </FormControl>

                                {/* Stroke */}
                                <FormControl className="mb-3">
                                    <FormLabel>Stroke</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("stroke")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Stomach or Peptic Ulcer */}
                                <FormControl className="mb-3">
                                    <FormLabel>
                                        Stomach or Peptic Ulcer
                                    </FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("stomach_peptic_ulcer")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Systemic lupus
                            erythematous(Lupus) */}
                                <FormControl className="mb-3">
                                    <FormLabel>
                                        Systemic lupus erythematous(Lupus)
                                    </FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("lupus_erythematous")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Epilepsy */}
                                <FormControl className="mb-3">
                                    <FormLabel>Epilepsy</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("epilepsy")}
                                        label="Yes"
                                    />
                                </FormControl>
                            </div>

                            {/* Group 3 */}
                            <div className="col-4">
                                {/* Rheumatic Fever */}
                                <FormControl className="mb-3">
                                    <FormLabel>Rheumatic Fever</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("rheumatic_fever")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Congestive Heart
                            Failure */}
                                <FormControl className="mb-3">
                                    <FormLabel>
                                        Congestive Heart Failure
                                    </FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register(
                                            "congestive_heart_failure"
                                        )}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Cataracts */}
                                <FormControl className="mb-3">
                                    <FormLabel>Cataracts</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("cataracts")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Tuberculosis */}
                                <FormControl className="mb-3">
                                    <FormLabel>Tuberculosis</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("tuberculosis")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Angina */}
                                <FormControl className="mb-3">
                                    <FormLabel>Angina</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("angina")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Kidney Disease */}
                                <FormControl className="mb-3">
                                    <FormLabel>Kidney Disease</FormLabel>
                                    <Checkbox
                                        label="Yes"
                                        type="checkbox"
                                        {...register("kidney_disease")}
                                    />
                                </FormControl>

                                {/* HIV/AIDS */}
                                <FormControl className="mb-3">
                                    <FormLabel>HIV/AIDS</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("hiv_aids")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Colitis */}
                                <FormControl className="mb-3">
                                    <FormLabel>Colitis</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("colitis")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Kidney Stones */}
                                <FormControl className="mb-3">
                                    <FormLabel>Kidney Stones</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("kidney_stones")}
                                        label="Yes"
                                    />
                                </FormControl>

                                {/* Psychiatric Illness */}
                                <FormControl className="mb-3">
                                    <FormLabel>Psychiatric Illness</FormLabel>
                                    <Checkbox
                                        type="checkbox"
                                        {...register("psychiatric_illness")}
                                        label="Yes"
                                    />
                                </FormControl>
                            </div>
                        </SimpleGrid>

                        {/* Row 9 */}
                        <SimpleGrid
                            columns={{ base: 1, md: 1 }}
                            spacing={4}
                            mb={6}
                        >
                            <div className="col-12 mb-3">
                                <FormControl className="mb-3">
                                    <FormLabel>
                                        Other Medical History (optional)
                                    </FormLabel>
                                    <Input type="text" {...register("other")} />
                                </FormControl>
                            </div>
                        </SimpleGrid>

                        <hr className="mb-2" />

                        {/* Allergies */}
                        <Heading as="h5" size="lg" my={3}>
                            Allergies
                        </Heading>

                        {/* Row 10 */}
                        <SimpleGrid
                            columns={{ base: 1, md: 2 }}
                            spacing={4}
                            mb={6}
                        >
                            {/* Drug Allergies */}
                            <Box>
                                <FormControl className="mb-3">
                                    <FormLabel>
                                        Drug Allergies (optional)
                                    </FormLabel>
                                    <Input
                                        type="text"
                                        {...register("drug_allergy_details")}
                                    />
                                </FormControl>
                            </Box>

                            {/* Food Allergies */}
                            <Box>
                                <FormControl className="mb-3">
                                    <FormLabel>
                                        Food Allergies (optional)
                                    </FormLabel>
                                    <Input
                                        type="text"
                                        {...register("food_allergy_details")}
                                    />
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        <SimpleGrid>
                            <Box>
                                <Button
                                    fontSize="sm"
                                    variant="outline"
                                    fontWeight="500"
                                    type="submit"
                                    w="20%"
                                    h="50"
                                    mb="24px"
                                    isLoading={isLoading}
                                    loadingText="Loading..."
                                    spinner={<Spinner />}
                                >
                                    Update
                                </Button>
                                {isError && (
                                    <Text color="red.500">
                                        Failed to update patient
                                    </Text>
                                )}
                                {isSuccess && (
                                    <Text color="green.500">
                                        Patient updated successfully
                                    </Text>
                                )}
                            </Box>
                        </SimpleGrid>
                    </form>
                </CardBody>
            </Card>
        </Box>
    );
};

export default UpdatePatient;
